import React from "react"
import { HubspotProvider } from "@aaronhayes/react-use-hubspot-form"
import "./src/styles/global.scss"
import ChatbaseWidget from "./src/integrations/ChatBase/chatbaseWidget"
import { GrowthBookProvider } from "@growthbook/growthbook-react"
import growthbook from "./src/integrations/GrowthBook/GrowthBook"
import GrowthBookInit from "./src/integrations/GrowthBook/GrowthBookInit"
import { captureClickIds } from "./src/analytics/clickIds"

let isFirstRender = true
const OFFSET = 100

// Robust Segment load trigger.
// gatsby-plugin-segment-js (delayLoad: true) only arms analytics.js loading on the
// first `scroll`, so visits without a scroll never send a pageview. We broaden the
// trigger to the first user intent OR an idle fallback, calling the loader the plugin
// exposes globally. Perf-neutral: loading stays post-paint, never during critical render.
const SEGMENT_INTENT_EVENTS = [
  "scroll",
  "mousemove",
  "mousedown",
  "keydown",
  "touchstart",
  "pointerdown",
  "visibilitychange"
]
const SEGMENT_IDLE_FALLBACK_MS = 1500

const triggerSegmentLoad = () => {
  if (typeof window === "undefined") return
  // segmentSnippetLoader is idempotent (guarded by segmentSnippetLoaded/Loading)
  if (typeof window.segmentSnippetLoader === "function") {
    window.segmentSnippetLoader()
  }
}

export const onClientEntry = () => {
  if (typeof window === "undefined") return

  // Avant le chargement de Segment : l'URL d'atterrissage porte les click IDs
  // et elle peut etre reecrite par la navigation cote client.
  captureClickIds()

  let fired = false
  const fireOnce = () => {
    if (fired) return
    fired = true
    SEGMENT_INTENT_EVENTS.forEach(evt =>
      window.removeEventListener(evt, fireOnce)
    )
    triggerSegmentLoad()
  }

  SEGMENT_INTENT_EVENTS.forEach(evt =>
    window.addEventListener(evt, fireOnce, { passive: true })
  )

  // Fallback for zero-interaction visits: load when the browser is idle (post-paint),
  // so no impact on LCP/TBT/CLS.
  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(fireOnce, { timeout: SEGMENT_IDLE_FALLBACK_MS })
  } else {
    setTimeout(fireOnce, SEGMENT_IDLE_FALLBACK_MS)
  }
}

// Scroll vers un élément avec offset
const scrollToElement = el => {
  if (!el) return

  const rect = el.getBoundingClientRect()
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop
  const topPosition = scrollTop + rect.top - OFFSET

  window.scrollTo({
    top: topPosition,
    behavior: "smooth"
  })
}

// Gestion des clics sur ancres internes
const handleAnchorClick = e => {
  const href = e.currentTarget.getAttribute("href")
  if (!href || !href.startsWith("#")) return

  e.preventDefault()

  const id = href.slice(1)
  const el = document.getElementById(id)
  scrollToElement(el)
}

// Attacher le listener à tous les liens d’ancres
const attachAnchorListeners = () => {
  document.querySelectorAll('a[href^="#"]').forEach(a => {
    a.removeEventListener("click", handleAnchorClick) // évite doublons
    a.addEventListener("click", handleAnchorClick)
  })
}

export const onRouteUpdate = ({ location, prevLocation }) => {
  // clear the page data
  // this is done so that the fresh data is used for the tracking event in usePageTracking hook
  if (isFirstRender) {
    isFirstRender = false
  } else {
    window.__agicap_page_data = null
  }

  const appHeight = () => {
    const doc = document.documentElement
    doc.style.setProperty("--app-height", `${window.innerHeight}px`)
  }
  window.addEventListener("resize", appHeight)
  appHeight()

  attachAnchorListeners()

  // If there's a hash in the URL, scroll to that element with offset
  if (window.location.hash) {
    const el = document.getElementById(window.location.hash.slice(1))
    if (el) {
      // Reset scroll position to top before scrolling to element
      window.scrollTo(0, 0)
      // Timeout  to allow the page to render
      setTimeout(() => scrollToElement(el), 50)
    }
  }

  if (typeof window !== "undefined" && window.growthbook) {
    try {
      window.growthbook.setAttributes({
        ...window.growthbook.getAttributes(),
        url: window.location.href,
        path: window.location.pathname,
        deviceType: window.innerWidth < 768 ? "mobile" : "desktop"
      })
    } catch (error) {
      console.warn("Failed to update GrowthBook attributes:", error)
    }
  }
}

// Wrap root element with HubspotProvider
export const wrapRootElement = ({ element }) => {
  if (typeof window !== "undefined") {
    window.growthbook = growthbook
  }

  return (
    <HubspotProvider>
      <GrowthBookProvider growthbook={growthbook}>
        <GrowthBookInit />
        {element}
      </GrowthBookProvider>
      <ChatbaseWidget />
    </HubspotProvider>
  )
}
